import { useHandleResponse } from '@/utils/useHandleResponse';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, DatePicker, Input, Modal, Select, Spin, Table } from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from '../../../../context/AuthProvider';
import { setUsers } from '../../../../contextRedux/User/UserStore';
import InterventionFileService from '../../../../services/interventionFile/InterventionFileService';
import MentenancePlanningService from '../../../../services/mentenancePlanning/MentenancePlanningService';
import UserService from '../../../../services/user/UserService';
import { RootState } from '../../../../store';

const MaintenanceModal: React.FC<any> = ({
  isModalVisible,
  handleModalClose,
  selectedRecord,
  handleViewRecord,
  onUpdate,
  haveActions = false,
}) => {
  const dispatch = useDispatch();

  const users = useSelector((state: RootState) => state?.store?.user?.data);

  const [isReasonModalVisible, setIsReasonModalVisible] = useState(false);
  const [reason, setReason] = useState('');
  const [assignedTechnician, setAssignedTechnician] = useState<any>(selectedRecord?.assignedTechnician);
  const [newAppointmentDate, setNewAppointmentDate] = useState<any>(selectedRecord?.appointment);
  const { profile } = useAuth();
  const { message } = App.useApp();
  const handleResponse = useHandleResponse();

  const { data: _users, isFetching: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: 'tehnician , senior_tehnician',
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  useEffect(() => {
    setNewAppointmentDate(selectedRecord?.appointment);
  }, [selectedRecord?.appointment]);

  const sanitizeFilename = (value: string) =>
    value
      .replace(/[\\/:*?"<>|]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const buildPdfFilename = (record: any) => {
    const companyName = record?.company?.name ?? 'Client';
    const workPlaceName = record?.workPlace?.name ?? 'Workplace';
    const fileNumber = record?.interventionFile?.number ?? record?.number ?? '';
    const base =
      !companyName || !workPlaceName || !fileNumber
        ? record?.interventionFileId ?? record?.id ?? 'file'
        : `${companyName} ${workPlaceName} ${fileNumber}`;
    return `${sanitizeFilename(base)}.pdf`;
  };

  const { mutate: openInterventionFilePdf } = useMutation<
    Response,
    Error,
    { interventionFileId: string; record: any; targetWindow: Window | null }
  >({
    mutationFn: ({ interventionFileId }) => InterventionFileService.print(interventionFileId),
    onSuccess: async (response, { record, targetWindow }) => {
      if (!response.ok) {
        message.error('Eroare la încărcarea fișierului PDF.');
        return;
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const pdfFilename = buildPdfFilename(record);
      const escapedFilename = pdfFilename.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>PDF - ${escapedFilename}</title></head><body style="margin:0;padding:0;overflow:hidden;"><iframe src="${blobUrl}" style="width:100%;height:100vh;border:none;"></iframe><div style="position:fixed;top:10px;right:10px;z-index:9999;background:rgba(255,255,255,0.95);padding:8px 12px;border-radius:4px;box-shadow:0 2px 8px rgba(0,0,0,0.15);"><a href="${blobUrl}" download="${escapedFilename}" style="text-decoration:none;color:#1890ff;font-weight:500;">Descarcă PDF</a></div></body></html>`;
      const htmlBlob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
      const htmlBlobUrl = window.URL.createObjectURL(htmlBlob);
      if (targetWindow && !targetWindow.closed) {
        targetWindow.location.href = htmlBlobUrl;
      } else {
        window.open(htmlBlobUrl, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => window.URL.revokeObjectURL(htmlBlobUrl), 60_000);
    },
    onError: () => {
      message.error('Eroare la încărcarea fișierului PDF.');
    },
  });

  const handleOpenInterventionFilePdf = (record: any, fileNumber?: string) => {
    if (!record?.interventionFileId) return;
    const targetWindow = window.open('', '_blank');
    if (!targetWindow) {
      message.warning('Permiteți ferestrele pop-up pentru a deschide PDF-ul într-un tab nou.');
      return;
    }
    try {
      targetWindow.opener = null;
    } catch {}
    const recordForFilename = fileNumber ? { ...record, number: fileNumber } : record;
    openInterventionFilePdf({ interventionFileId: record.interventionFileId, record: recordForFilename, targetWindow });
  };

  const { mutate: saveMentenancePlanning, isPending: isSavingMentenancePlanning } = useMutation({
    mutationFn: (formData: any) => MentenancePlanningService.upsert(formData.id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data, `Mentenanța ${data?.response?.number} a fost reprogramată cu succes`);
        handleModalClose();
        onUpdate?.();
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  const handleSetAppointment = () => {
    if (!newAppointmentDate) {
      message.error('Selectati o data pentru programare.');
      return;
    }

    const updatedRecord = {
      ...selectedRecord,
      appointment: newAppointmentDate,
      assignedTechnicianId: assignedTechnician?.id ?? selectedRecord?.assignedTechnician?.id,
      statusId: 2,
      history: [...selectedRecord.history, `[Reprogramata] - ${dayjs(newAppointmentDate).format('DD-MM-YYYY')}`],
    };
    saveMentenancePlanning(updatedRecord); // Save the updated record
  };

  // Function to show the reason input modal
  const showReasonModal = () => {
    setIsReasonModalVisible(true);
  };

  // Function to handle the reason submission
  const handleReasonSubmit = () => {
    if (reason.trim() === '') {
      message.error('Introduceti un motiv pentru reprogramarea mentenantei.');
      return;
    }
    // Here you would handle submitting the reason (e.g., API call or other logic)
    handleViewRecord(selectedRecord, 5, reason);
    setIsReasonModalVisible(false);
    setReason(''); // Reset the reason field after submission
  };

  // Function to handle cancellation of the reason modal
  const handleReasonCancel = () => {
    setIsReasonModalVisible(false);
    setReason(''); // Reset the reason field
  };

  const columns = [
    {
      title: 'Field',
      dataIndex: 'field',
      key: 'field',
      render: (text: any) => <strong>{text}</strong>,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
    },
  ];

  const data = [
    {
      key: '1',
      field: 'Numar',
      value: selectedRecord?.number || '-',
    },
    {
      key: '2',
      field: 'Inregistrata de',
      value: selectedRecord?.registeredByUser?.name || '-',
    },
    {
      key: '3',
      field: 'Beneficiar',
      value: selectedRecord?.company?.name || '-',
    },
    {
      key: '4',
      field: 'Punct',
      value: selectedRecord?.workPlace?.name || '-',
    },
    {
      key: '5',
      field: 'Adresa',
      value: selectedRecord?.workPlace?.address || '-',
    },
    {
      key: '6',
      field: 'Harta',
      value: (
        <div>
          {/* Google Maps link with icon */}
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
              selectedRecord?.workPlace?.address
            )}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='https://lh3.googleusercontent.com/9tLfTpdILdHDAvGrRm7GdbjWdpbWSMOa0csoQ8pUba9tLP8tq7M4Quks1xuMQAVnAxVfryiDXRzZ-KDnkPv8Sm4g_YFom1ltQHjQ6Q'
              alt='Google Maps'
              style={{ width: '44px', height: '44px', marginRight: '8px' }}
            />
          </a>
          {/* Waze link with icon */}
          <a
            href={`https://www.waze.com/ro/live-map/directions?to=address:${encodeURIComponent(
              selectedRecord?.workPlace?.address
            )}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            <img
              src='https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/cd/87/6d/cd876daf-4c14-2ca0-05d7-6586ed8b6833/AppIcon-0-0-1x_U007emarketing-0-7-0-85-220.png/230x0w.webp'
              alt='Waze'
              style={{ width: '44px', height: '44px', marginRight: '8px' }}
            />
          </a>
        </div>
      ),
    },
    {
      key: '7',
      field: 'Sisteme',
      value:
        selectedRecord?.mentenancePlanningSystemTypes?.map((type: any) => `[${type.systemType.name}]`).join(' ') || '-',
    },
    {
      key: '8',
      field: 'Asignata lui',
      value: (
        <div>
          {selectedRecord?.statusId === 5 && profile?.userPermission?.rols.includes('dispatcher') ? (
            <Select
              value={assignedTechnician?.name ?? selectedRecord?.assignedTechnician?.name}
              style={{ width: '100%' }}
              placeholder='Select Tehnician'
              showSearch
              onChange={(value) => {
                setAssignedTechnician(users.find((u: any) => u.name === value));
              }}
              filterOption={(input, option) =>
                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {users?.map((user: any) => (
                <Select.Option key={user?.id} value={user?.name}>
                  {user?.name}
                </Select.Option>
              ))}
            </Select>
          ) : (
            <span>{selectedRecord?.assignedTechnician?.name}</span>
          )}
        </div>
      ),
    },
    {
      key: '9',
      field: 'Perioada programata',
      value: (
        <div>
          {selectedRecord?.statusId === 5 && profile?.userPermission?.rols.includes('dispatcher') ? (
            <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
              <DatePicker
                value={newAppointmentDate ? dayjs(newAppointmentDate) : dayjs()}
                style={{ width: '50%', marginRight: '8px' }}
                placeholder='Selectati o data noua'
                format={'DD-MM-YYYY'}
                onChange={(date) => setNewAppointmentDate(date ? date.format('YYYY-MM-DD') : null)} // Store the temporary date
              />
            </div>
          ) : (
            <span>{moment(selectedRecord?.appointment).format('DD-MM-YYYY')}</span>
          )}
        </div>
      ),
    },
    {
      key: '10',
      field: 'Status',
      value: selectedRecord?.status?.name || '-',
    },
    {
      key: '11',
      field: 'Log-uri',
      value: (
        <div>
          {selectedRecord?.history?.map((entry: string, index: number) => {
            // Regex to match "Numar fisa: <number>"
            const match = entry.match(/FISA: (\d+)/);
            if (match) {
              // Extract the number
              return (
                <React.Fragment key={index}>
                  <a
                    href='#'
                    target='_blank'
                    rel='noopener noreferrer'
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpenInterventionFilePdf(selectedRecord, match[1]);
                    }}
                  >
                    {entry}
                  </a>
                </React.Fragment>
              );
            }
            // If no match, render as plain text
            return (
              <React.Fragment key={index}>
                {entry}
                <br />
              </React.Fragment>
            );
          }) || '-'}
        </div>
      ),
    },
    selectedRecord?.statusId === 5 &&
      profile?.userPermission?.rols.includes('dispatcher') && {
        key: '12',
        field: 'Reprogramare',
        value: (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {
              <Button
                type='primary'
                onClick={handleSetAppointment} // Save the updated record on button click
              >
                Reprogrameaza
              </Button>
            }
          </div>
        ),
      },
    haveActions && {
      key: '13',
      field: 'Actiuni',
      value: (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {(selectedRecord?.statusId == 2 || selectedRecord?.statusId == 5) && (
            <Button onClick={showReasonModal} style={{ backgroundColor: '#e52b50', color: 'white' }}>
              {selectedRecord?.statusId == 5 ? 'Reprogramare' : 'Cerere reprogramare'}
            </Button>
          )}
          {selectedRecord?.statusId == 2 && (
            <Button
              style={{ backgroundColor: '#008080', color: 'white' }}
              onClick={() => handleViewRecord(selectedRecord, 3)}
              disabled={selectedRecord?.statusId == 3}
            >
              Am ajuns
            </Button>
          )}
          {selectedRecord?.statusId == 3 && (
            <Button
              style={{ backgroundColor: '#1677ff', color: 'white' }}
              onClick={() => handleViewRecord(selectedRecord, 4)}
            >
              Am Terminat
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (isSavingMentenancePlanning || isUsersLoading) {
    return <Spin className='spinner' size='large' />;
  }

  return (
    <>
      <Modal title='Informatii mentenanta' open={isModalVisible} onCancel={handleModalClose} footer={null} width={800}>
        {selectedRecord && (
          <Table
            columns={columns}
            dataSource={data}
            pagination={false}
            showHeader={false}
            rowKey={selectedRecord.id}
            style={{ marginTop: '10px' }}
            scroll={{ x: 'max-content' }}
          />
        )}
      </Modal>

      <Modal
        title='Motiv pentru reprogramare'
        open={isReasonModalVisible}
        onCancel={handleReasonCancel}
        onOk={handleReasonSubmit}
        okText='Submit'
        cancelText='Cancel'
      >
        <Input.TextArea
          rows={4}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder='Introduceti motivul reprogramarii...'
        />
      </Modal>
    </>
  );
};

export default MaintenanceModal;
